import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { wizards as wizardsApi, vaultTokens as vaultApi, uploads as uploadsApi } from '@/utils/api';
import { extractVideoThumbnail } from '@/utils/videoThumbnail';
import SignatureCanvas from 'react-signature-canvas';
import {
  Zap, ArrowLeft, ArrowRight, Save, Check, CheckCircle2, Lock, ExternalLink,
  Plus, Trash2, Copy, ShieldCheck, FileText, X, UploadCloud, Video, Image as ImageIcon, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------- Conditional logic ----------------
function shouldShow(field, allValues) {
  const c = field.show_if;
  if (!c || !c.field) return true;
  // flatten all section values into one lookup
  const lookup = {};
  Object.values(allValues || {}).forEach(sec => Object.assign(lookup, sec || {}));
  const v = lookup[c.field];
  if (c.op === 'equals') return String(v || '') === String(c.value || '');
  if (c.op === 'contains') {
    if (Array.isArray(v)) return v.includes(c.value);
    return (v || '').toString().toLowerCase().includes(String(c.value || '').toLowerCase());
  }
  if (c.op === 'truthy') return !!v;
  return true;
}

// ---------------- Field renderers ----------------
function VaultListField({ value = [], onChange, field, slug, sessionId, brandColor }) {
  const [modal, setModal] = useState(null); // {label, type}
  const [newCred, setNewCred] = useState({ label: '', type: 'login', notes: '' });

  const issueNew = async () => {
    if (!newCred.label.trim()) return toast.error('Label required');
    try {
      const r = await vaultApi.issue(slug, sessionId, newCred.label, newCred.type, newCred.notes);
      const url = `${window.location.origin}/vault-submit/${r.data.token}`;
      onChange([...value, { id: r.data.id, label: newCred.label, type: newCred.type, notes: newCred.notes, url, token: r.data.token, submitted: false }]);
      setModal(null);
      setNewCred({ label: '', type: 'login', notes: '' });
      toast.success('Secure link created — share or fill it now');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success('Copied to clipboard'); };
  const remove = (id) => onChange(value.filter(v => v.id !== id));

  return (
    <div>
      <div className="p-3 rounded-btn mb-2" style={{ background: '#E6F1F4', border: '1px solid #B8D6DF' }}>
        <div className="flex items-start gap-2">
          <ShieldCheck size={16} style={{ color: brandColor, marginTop: 2 }} />
          <div className="text-xs" style={{ color: '#1A2428' }}>
            <p className="font-semibold mb-0.5">End-to-end encrypted</p>
            <p style={{ color: '#5C6B72' }}>Each credential below is stored via a one-time secure link. Even we can't read it until you submit. Link expires in 48 hours.</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {value.map(v => (
          <div key={v.id} className="rounded-btn p-2.5 flex items-center justify-between" style={{ background: 'white', border: '1px solid #E8E3D9' }} data-testid={`vault-cred-${v.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Lock size={12} style={{ color: brandColor }} />
                <p className="text-sm font-semibold" style={{ color: '#1A2428' }}>{v.label}</p>
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#E6F1F4', color: brandColor }}>{v.type}</span>
                {v.submitted && <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>submitted</span>}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1" style={{ color: brandColor }} data-testid={`vault-link-${v.id}`}>
                  <ExternalLink size={10} /> Open secure page
                </a>
                <button type="button" onClick={() => copyUrl(v.url)} className="text-xs flex items-center gap-1" style={{ color: '#5C6B72' }}>
                  <Copy size={10} /> Copy link
                </button>
              </div>
            </div>
            <button type="button" onClick={() => remove(v.id)} className="p-1.5" style={{ color: '#B34A3A' }}><Trash2 size={13} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setModal(true)} className="text-sm flex items-center gap-1.5 px-3 py-2 rounded-btn font-semibold transition-colors"
                style={{ background: brandColor, color: 'white' }} data-testid="add-credential-button">
          <Plus size={13} /> Add credential
        </button>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-md rounded-card p-5" style={{ background: 'white' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: '#1A2428' }}>Add a credential</h3>
              <button type="button" onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: '#5C6B72' }}>What is it?</label>
                <input className="input-agency text-sm" value={newCred.label} placeholder="e.g. Salesforce login" onChange={e => setNewCred({...newCred, label: e.target.value})} data-testid="new-cred-label" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: '#5C6B72' }}>Type</label>
                <select className="input-agency text-sm" value={newCred.type} onChange={e => setNewCred({...newCred, type: e.target.value})}>
                  <option value="login">Username + password</option>
                  <option value="api_key">API key</option>
                  <option value="password">Password only</option>
                  <option value="note">Secure note</option>
                </select>
              </div>
              <textarea className="input-agency text-sm resize-none" rows={2} placeholder="Notes for us (optional)" value={newCred.notes} onChange={e => setNewCred({...newCred, notes: e.target.value})} />
              <div className="flex gap-3">
                <button type="button" className="flex-1 py-2 text-sm rounded-btn border" style={{ borderColor: '#E8E3D9', color: '#5C6B72' }} onClick={() => setModal(null)}>Cancel</button>
                <button type="button" className="flex-1 text-white py-2 text-sm rounded-btn font-semibold" style={{ background: brandColor }} onClick={issueNew} data-testid="create-cred-link">Create secure link</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FileListField({ value = [], onChange, slug, sessionId, fieldId, field, brandColor }) {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);
  const acceptHint = (field?.accept) || ((field?.type === 'video_upload') ? 'video/*' : undefined);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const newItems = [];
    for (const file of files) {
      try {
        const r = await uploadsApi.wizardUpload(slug, sessionId, file, fieldId);
        const item = {
          id: r.data.id,
          url: r.data.public_url,
          name: r.data.original_name,
          content_type: r.data.content_type,
          size: r.data.size,
          thumbnail_url: null,
        };
        // For videos, extract + upload thumbnail + attach
        if (file.type && file.type.startsWith('video/')) {
          try {
            const thumb = await extractVideoThumbnail(file, 1.0);
            const thumbFile = new File([thumb.blob], `${file.name}.thumb.jpg`, { type: 'image/jpeg' });
            // Upload thumbnail as a tagged asset
            const fd = new FormData();
            fd.append('file', thumbFile);
            fd.append('slug', slug);
            fd.append('session_id', sessionId);
            if (fieldId) fd.append('field_id', `${fieldId}__thumb`);
            const thumbResp = await uploadsApi.wizardUpload(slug, sessionId, thumbFile, `${fieldId || 'v'}__thumb`);
            await uploadsApi.setWizardThumbnail(slug, sessionId, item.id, thumbResp.data.key, thumbResp.data.public_url, thumb.durationSec);
            item.thumbnail_url = thumbResp.data.public_url;
            item.duration_s = thumb.durationSec;
          } catch (err) {
            console.warn('Thumbnail extraction failed:', err);
          }
        }
        newItems.push(item);
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || 'upload failed'}`);
      }
    }
    if (newItems.length) onChange([...value, ...newItems]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const addUrl = () => {
    if (!url.trim()) return;
    onChange([...value, { url: url.trim(), name: url.split('/').pop() }]);
    setUrl('');
  };

  const kindIcon = (ct) => {
    if (!ct) return <FileText size={12} />;
    if (ct.startsWith('video/')) return <Video size={12} />;
    if (ct.startsWith('image/')) return <ImageIcon size={12} />;
    return <FileText size={12} />;
  };

  return (
    <div>
      <div
        className="rounded-btn p-4 text-center cursor-pointer transition-colors"
        style={{ background: 'white', border: '2px dashed #B8D6DF' }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(Array.from(e.dataTransfer.files || [])); }}
        data-testid={`wizard-upload-dropzone-${fieldId || 'file'}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptHint}
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          data-testid={`wizard-upload-input-${fieldId || 'file'}`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm" style={{ color: brandColor }}>
            <Loader2 size={14} className="animate-spin" /> Uploading…
          </div>
        ) : (
          <div>
            <UploadCloud size={22} style={{ color: brandColor, margin: '0 auto 6px' }} />
            <p className="text-sm font-semibold" style={{ color: '#1A2428' }}>Drop files here or click to browse</p>
            <p className="text-xs mt-0.5" style={{ color: '#5C6B72' }}>Up to 100MB — videos, images, PDFs, docs</p>
          </div>
        )}
      </div>

      <details className="mt-2">
        <summary className="text-xs cursor-pointer" style={{ color: '#5C6B72' }}>Or paste a shareable URL (Drive/Dropbox/Loom)</summary>
        <div className="flex gap-2 mt-2">
          <input className="input-agency text-sm flex-1" placeholder="https://..." value={url} onChange={e => setUrl(e.target.value)} />
          <button type="button" className="btn-primary px-3 text-xs" onClick={addUrl}>Add link</button>
        </div>
      </details>

      <div className="mt-2 space-y-1.5">
        {value.map((f, i) => (
          <div key={f.id || i} className="p-2 rounded-btn text-xs" style={{ background: 'white', border: '1px solid #E8E3D9' }}>
            <div className="flex items-center justify-between">
              <a href={f.url} target="_blank" rel="noreferrer" className="truncate flex items-center gap-1.5 flex-1 min-w-0" style={{ color: brandColor }}>
                {kindIcon(f.content_type)}
                <span className="truncate">{f.name || f.url}</span>
                {f.size && <span className="text-[10px] flex-shrink-0" style={{ color: '#8A99A0' }}>· {(f.size / 1024).toFixed(0)}kb</span>}
              </a>
              <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="ml-2" style={{ color: '#B34A3A' }}><Trash2 size={11} /></button>
            </div>
            {f.content_type && f.content_type.startsWith('video/') && (
              f.thumbnail_url ? (
                <div className="mt-1.5 relative rounded-btn overflow-hidden" style={{ maxHeight: 200 }}>
                  <img src={f.thumbnail_url} alt={f.name} className="w-full object-cover" style={{ maxHeight: 200 }} />
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(91,45,142,0.9)' }}>
                      <Video size={16} style={{ color: 'white' }} />
                    </div>
                  </div>
                  {f.duration_s && (
                    <span className="absolute bottom-1 right-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}>
                      {Math.floor(f.duration_s / 60)}:{String(Math.floor(f.duration_s % 60)).padStart(2, '0')}
                    </span>
                  )}
                </div>
              ) : (
                <video src={f.url} controls className="mt-1.5 w-full rounded-btn" style={{ maxHeight: 240, background: 'black' }} />
              )
            )}
            {f.content_type && f.content_type.startsWith('image/') && (
              <img src={f.url} alt={f.name} className="mt-1.5 w-full rounded-btn" style={{ maxHeight: 200, objectFit: 'cover' }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, slug, sessionId, allValues, brandColor }) {
  const common = {
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    placeholder: field.placeholder || '',
    className: 'input-agency text-sm',
    required: !!field.required,
  };
  if (field.type === 'textarea') return <textarea {...common} rows={4} className="input-agency text-sm resize-none" />;
  if (field.type === 'email') return <input {...common} type="email" />;
  if (field.type === 'tel') return <input {...common} type="tel" />;
  if (field.type === 'url') return <input {...common} type="url" />;
  if (field.type === 'number') return <input {...common} type="number" />;
  if (field.type === 'date') return <input {...common} type="date" />;
  if (field.type === 'select') {
    return (
      <select {...common}>
        <option value="">Select…</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'radio') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {(field.options || []).map(o => (
          <label key={o} className="flex items-center gap-2 text-sm px-3 py-2 rounded-btn cursor-pointer transition-colors" style={{ border: '1px solid #E8E3D9', background: value === o ? '#E6F1F4' : 'white', color: '#1A2428' }}>
            <input type="radio" name={field.id} value={o} checked={value === o} onChange={e => onChange(e.target.value)} required={field.required} />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-start gap-2 text-sm" style={{ color: '#1A2428' }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} required={field.required} className="mt-1" />
        <span>{field.placeholder || field.label}</span>
      </label>
    );
  }
  if (field.type === 'checkbox_multi') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {(field.options || []).map(o => (
          <label key={o} className="flex items-center gap-2 text-sm px-3 py-2 rounded-btn cursor-pointer transition-colors" style={{ border: '1px solid #E8E3D9', background: arr.includes(o) ? '#E6F1F4' : 'white', color: '#1A2428' }}>
            <input type="checkbox" checked={arr.includes(o)} onChange={e => onChange(e.target.checked ? [...arr, o] : arr.filter(x => x !== o))} />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === 'vault_list') {
    return <VaultListField value={value || []} onChange={onChange} field={field} slug={slug} sessionId={sessionId} brandColor={brandColor} />;
  }
  if (field.type === 'file_multi' || field.type === 'video_upload' || field.type === 'file_upload') {
    return <FileListField value={value || []} onChange={onChange} slug={slug} sessionId={sessionId} fieldId={field.id} field={field} brandColor={brandColor} />;
  }
  return <input {...common} type="text" />;
}

// ---------------- Signature Pad ----------------
function SignatureStep({ wizard, onSign, brandColor }) {
  const [mode, setMode] = useState(wizard.allow_signature_draw ? 'draw' : 'typed');
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const sigRef = useRef(null);

  const handleSubmit = () => {
    if (!agreed) return toast.error('Please agree to the terms');
    if (mode === 'draw') {
      if (!sigRef.current || sigRef.current.isEmpty()) return toast.error('Please draw your signature');
      const dataUrl = sigRef.current.toDataURL('image/png');
      onSign({ drawn: dataUrl, typed: null, agreed: true });
    } else {
      if (!typedName.trim()) return toast.error('Type your full name');
      onSign({ drawn: null, typed: typedName.trim(), agreed: true });
    }
  };

  return (
    <div className="space-y-4">
      {wizard.terms_md && (
        <div className="rounded-card p-4 whitespace-pre-wrap text-sm" style={{ background: 'white', border: '1px solid #E8E3D9', color: '#1A2428', maxHeight: 280, overflow: 'auto' }}>
          {wizard.terms_md}
        </div>
      )}
      <div className="flex gap-2">
        {wizard.allow_signature_draw && (
          <button type="button" className="px-3 py-1.5 text-xs rounded-btn font-semibold" style={{ background: mode === 'draw' ? brandColor : 'white', color: mode === 'draw' ? 'white' : '#5C6B72', border: '1px solid #E8E3D9' }} onClick={() => setMode('draw')} data-testid="sig-mode-draw">Draw signature</button>
        )}
        {wizard.allow_signature_typed && (
          <button type="button" className="px-3 py-1.5 text-xs rounded-btn font-semibold" style={{ background: mode === 'typed' ? brandColor : 'white', color: mode === 'typed' ? 'white' : '#5C6B72', border: '1px solid #E8E3D9' }} onClick={() => setMode('typed')} data-testid="sig-mode-typed">Type name</button>
        )}
      </div>

      {mode === 'draw' ? (
        <div>
          <div className="rounded-btn overflow-hidden" style={{ background: 'white', border: '1px solid #E8E3D9' }}>
            <SignatureCanvas ref={sigRef} penColor="black" canvasProps={{ width: 600, height: 180, className: 'w-full', 'data-testid': 'signature-canvas' }} />
          </div>
          <button type="button" className="text-xs mt-1" style={{ color: '#5C6B72' }} onClick={() => sigRef.current?.clear()}>Clear</button>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: '#5C6B72' }}>Type your full legal name</label>
          <input className="input-agency text-sm" value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="John Doe" data-testid="signature-typed-input" style={{ fontFamily: 'cursive', fontSize: 20 }} />
        </div>
      )}

      <label className="flex items-start gap-2 text-sm" style={{ color: '#1A2428' }}>
        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1" data-testid="terms-agree" />
        <span>I have read and agree to the terms above. My name, timestamp, IP address, and browser will be recorded as legally binding evidence.</span>
      </label>

      <button type="button" className="w-full py-3 rounded-btn text-white font-semibold text-sm transition-opacity" style={{ background: brandColor }} onClick={handleSubmit} disabled={!agreed} data-testid="wizard-submit-button">
        Submit for review
      </button>
    </div>
  );
}

// ---------------- Main Wizard ----------------
export default function Wizard() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);  // {wizard, brand}
  const [sessionId, setSessionId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState({});
  const [meta, setMeta] = useState({ email: '', name: '', company: '' });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // Load wizard (and optionally resume a draft)
  useEffect(() => {
    wizardsApi.public(slug).then(r => setData(r.data)).catch(() => setData('error'));
  }, [slug]);

  useEffect(() => {
    if (!data || data === 'error') return;
    const existing = searchParams.get('s');
    if (existing) {
      wizardsApi.getDraft(slug, existing)
        .then(r => {
          setSessionId(existing);
          setValues(r.data.data || {});
          setMeta({ email: r.data.email || '', name: r.data.name || '', company: r.data.company || '' });
          setStepIndex(r.data.current_step || 0);
        })
        .catch(() => {/* stale draft -> just ignore */});
    } else {
      wizardsApi.startDraft(slug, '').then(r => {
        setSessionId(r.data.session_id);
        setSearchParams({ s: r.data.session_id }, { replace: true });
      });
    }
    // eslint-disable-next-line
  }, [data]);

  const wizard = data && data !== 'error' ? data.wizard : null;
  const brand = data && data !== 'error' ? data.brand : null;
  const brandColor = brand?.primary_color || '#006080';
  const sections = wizard?.sections || [];
  const totalSteps = sections.length + 1; // +1 for signature
  const isSignatureStep = stepIndex >= sections.length;
  const currentSection = !isSignatureStep ? sections[stepIndex] : null;

  const setSectionValue = (secId, fid, v) => {
    setValues(prev => ({ ...prev, [secId]: { ...(prev[secId] || {}), [fid]: v } }));
  };

  const saveProgress = async (step = stepIndex) => {
    if (!sessionId) return;
    setSaving(true);
    try {
      await wizardsApi.saveDraft(slug, sessionId, {
        data: values, email: meta.email, name: meta.name, current_step: step,
      });
    } catch {/* ignore */} finally { setSaving(false); }
  };

  const canProceed = () => {
    if (!currentSection) return true;
    for (const f of currentSection.fields) {
      if (!shouldShow(f, values)) continue;
      if (f.required) {
        const v = (values[currentSection.id] || {})[f.id];
        if (f.type === 'checkbox' && !v) return false;
        if (Array.isArray(v) ? v.length === 0 : !v) return false;
      }
    }
    return true;
  };

  const next = async () => {
    if (!canProceed()) return toast.error('Please fill in all required fields');
    await saveProgress(stepIndex + 1);
    setStepIndex(s => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prev = async () => {
    await saveProgress(Math.max(stepIndex - 1, 0));
    setStepIndex(s => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitFinal = async ({ drawn, typed, agreed }) => {
    // Auto-detect name/email/company from basic section if present
    const basic = values.basic || {};
    const payload = {
      data: values,
      email: meta.email || basic.email,
      name: meta.name || basic.full_name,
      company: meta.company || basic.company_name,
      signature_drawn: drawn,
      signature_typed: typed,
      agreed_terms: !!agreed,
    };
    setSubmitting(true);
    try {
      const r = await wizardsApi.submit(slug, sessionId, payload);
      setDone(r.data.message || "Thanks — we've received your submission.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (data === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF8F4' }}><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} /></div>;
  if (data === 'error') return <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4' }}><p className="text-sm" style={{ color: '#5C6B72' }}>This onboarding link is no longer available.</p></div>;

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
        <div className="max-w-md w-full text-center p-8">
          <CheckCircle2 size={48} style={{ color: brandColor, margin: '0 auto 16px' }} />
          <h1 className="text-3xl font-black mb-2" style={{ color: '#1A2428' }}>All set!</h1>
          <p className="text-sm" style={{ color: '#5C6B72' }}>{done}</p>
        </div>
      </div>
    );
  }

  const resumeUrl = sessionId ? `${window.location.origin}/w/${slug}?s=${sessionId}` : '';

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: brandColor }}>
            <Zap size={14} color="white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-base" style={{ color: '#1A2428' }}>{brand?.name || 'Agency'}</span>
        </div>

        <h1 className="text-3xl font-black mb-1" style={{ color: '#1A2428' }} data-testid="wizard-title">{wizard.title}</h1>
        {wizard.description && <p className="text-sm mb-6" style={{ color: '#5C6B72' }}>{wizard.description}</p>}

        {/* Progress stepper */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            {[...sections, { id: 'sign', title: 'Sign' }].map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <div className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors" style={{ background: i <= stepIndex ? brandColor : '#E8E3D9', color: i <= stepIndex ? 'white' : '#5C6B72' }}>
                  {i < stepIndex ? <Check size={12} /> : i + 1}
                </div>
                {i < sections.length && <div className="flex-1 h-0.5 transition-colors" style={{ background: i < stepIndex ? brandColor : '#E8E3D9' }} />}
              </div>
            ))}
          </div>
          <p className="text-xs tabular-nums" style={{ color: '#5C6B72' }}>Step {stepIndex + 1} of {totalSteps}</p>
        </div>

        <div className="rounded-card p-6" style={{ background: 'white', border: '1px solid #E8E3D9' }}>
          {isSignatureStep ? (
            <>
              <h2 className="text-xl font-black mb-1" style={{ color: '#1A2428' }}>Terms & Signature</h2>
              <p className="text-sm mb-4" style={{ color: '#5C6B72' }}>Final step. Review the terms, add your signature, and submit.</p>
              <SignatureStep wizard={wizard} onSign={submitFinal} brandColor={brandColor} />
            </>
          ) : currentSection && (
            <>
              <h2 className="text-xl font-black mb-1" style={{ color: '#1A2428' }} data-testid={`section-title-${currentSection.id}`}>{currentSection.title}</h2>
              {currentSection.description && <p className="text-sm mb-4" style={{ color: '#5C6B72' }}>{currentSection.description}</p>}

              <div className="space-y-4">
                {currentSection.fields.filter(f => shouldShow(f, values)).map(f => (
                  <div key={f.id} data-testid={`field-wrap-${f.id}`}>
                    {f.type !== 'checkbox' && (
                      <label className="block text-sm font-semibold mb-1" style={{ color: '#1A2428' }}>
                        {f.label} {f.required && <span style={{ color: '#B34A3A' }}>*</span>}
                      </label>
                    )}
                    <FieldRenderer
                      field={f}
                      value={(values[currentSection.id] || {})[f.id]}
                      onChange={(v) => setSectionValue(currentSection.id, f.id, v)}
                      slug={slug}
                      sessionId={sessionId}
                      allValues={values}
                      brandColor={brandColor}
                    />
                    {f.note && <p className="text-xs mt-1" style={{ color: '#5C6B72' }}>{f.note}</p>}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Footer nav */}
          {!isSignatureStep && (
            <div className="mt-6 pt-4 flex items-center justify-between" style={{ borderTop: '1px solid #E8E3D9' }}>
              <button type="button" onClick={prev} disabled={stepIndex === 0} className="text-sm flex items-center gap-1 px-3 py-2 rounded-btn disabled:opacity-40" style={{ color: '#5C6B72' }} data-testid="wizard-prev-button">
                <ArrowLeft size={14} /> Back
              </button>
              <button type="button" onClick={() => saveProgress().then(() => toast.success('Progress saved'))} className="text-xs flex items-center gap-1 px-3 py-2" style={{ color: brandColor }}>
                <Save size={12} /> {saving ? 'Saving…' : 'Save for later'}
              </button>
              <button type="button" onClick={next} className="text-sm flex items-center gap-1 px-4 py-2 rounded-btn font-semibold text-white" style={{ background: brandColor }} data-testid="wizard-next-button">
                Next <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Resume link */}
        {resumeUrl && (
          <div className="mt-4 p-3 rounded-btn flex items-center gap-2 text-xs" style={{ background: 'white', border: '1px dashed #E8E3D9', color: '#5C6B72' }}>
            <Save size={12} /> <span className="truncate">Bookmark to resume later:</span>
            <code className="flex-1 font-mono truncate" style={{ color: '#1A2428' }}>{resumeUrl}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(resumeUrl); toast.success('Copied'); }} className="text-xs flex items-center gap-1" style={{ color: brandColor }}>
              <Copy size={10} /> Copy
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
